import { TAU } from '../core/math';
import { RNG, rr } from '../core/rng';
import { SFX } from '../platform/audio';
import { scene } from '../render/scene';
import { enemies } from '../entities/enemies';
import { angDiff, damageEnemy, nearestEnemy } from '../sim/fx';
import { disposeGadget, makeDrone, makeFieldRing, makeOrbitBlades, setBladeRadius, updateFieldRing } from '../render/gadgets';
import { spawnBullet } from '../sim/weapons';
import { GADGET_C, fxGadgetFire, fxLaunch, fxShockRing, fxSlashArc, fxSparks } from '../render/weapon-fx';
import { lockedTarget } from '../sim/lockon';
import { safeSpot } from '../world/arena';
import { P } from '../world/player';
import { mines, strikes } from '../world/pools';
/* ============================================================
   武器池（M5 三把，M6 補到六把）
   ============================================================

   **這些是自動開火的掛載，跟機體給的三把（主武裝 + 兩個主動技）分開。**
   機體那三把不進池子、不佔槽位 —— 見 GDD §7.1。

   每把武裝有自己的冷卻，由 sim/player.ts 逐一遞減並觸發。
   `cd(lv)` 與 `fire(lv)` 都吃等級，而且**至少要有一級是行為改變而不只是數字變大**
   （GDD §7.5）—— 每把的等級表寫在各自的註解裡。

   M5 先做三把證明管線（同時開火、各自冷卻、等級生效）。
   另外三把（斬波、環繞旋刃、感應無人機）是 M6 的工作。 */

/* 兩種武裝型態：

     定時觸發型  有 `cd(lv)` 與 `fire(lv)`，冷卻歸零就開一次（地雷、光柱、跳彈）
     持續型      有 `update(lv, dt, o)`，每步跑一次（環繞旋刃、無人機）

   兩者都可以有 `onEquip` / `onRemove` 來建立與收掉視覺物件。
   `o` 是槽位物件本身，持續型把自己的狀態掛在 `o.st` 上 ——
   狀態不能放在模組層級的變數，否則同一把武裝在下一場開局時會帶著上一場的殘留。 */
export type PoolWeapon = {
  id: string; n: string; kind: string;
  /** 這把吃哪 2–3 條統計軸。**刻意不是全部** ——
      VS 的 Whip 不吃 Speed 也不吃 Duration，那才逼玩家依武裝組合挑被動，
      而不是無腦堆同一種加成（見 .claude/notes/upgrade-redesign.md §1 規則三）。 */
  axes: string[];
  /** 行為質變發生在哪一級。卡片上會提示「下一級會變一把武器」。 */
  turn?: number;
  /** 質變的一句話說明。 */
  turnD?: string;
  d: (lv: number) => string;
  cd?: (lv: number) => number;
  fire?: (lv: number) => void;
  update?: (lv: number, dt: number, o: any) => void;
  onEquip?: (o: any) => void;
  onRemove?: (o: any) => void;
};

export var WEAPON_POOL: PoolWeapon[] = [
  {
    /* 磁暴地雷 —— 佈場型，逼玩家「先鋪路再把敵人拉過去」。
       **質變在 Lv3：接觸雷變成感應雷**（觸發範圍 ×2.2）。
       那一級之後它從「踩到才炸」變成「靠近就炸」，玩家的走位邏輯整個換掉：
       不用再把敵人精準拉到雷上，改成劃出一片不能進的區域。 */
    id: 'w-mine', n: '磁暴地雷', kind: '掛載', axes: ['範圍', '持續'],
    turn: 3, turnD: '接觸引爆 → 感應引爆（觸發範圍 ×2.2）',
    d: function (lv) {
      return '周圍佈設 ' + (2 + lv) + ' 顆地雷，' + (lv < 3 ? '接觸引爆' : '**感應引爆**')
        + '，傷害 ' + (22 + lv * 9);
    },
    cd: function (lv) { return Math.max(2.2, 5.0 - lv * 0.45); },
    fire: function (lv) {
      var n = 2 + lv, ar = P.s.area || 1;
      for (var k = 0; k < n; k++) {
        var mn = mines.get(); if (!mn) break;
        var a = (k / n) * TAU + rr(-0.3, 0.3), d = rr(4, 7.5);
        var sp = safeSpot(P.pos.x + Math.cos(a) * d, P.pos.z + Math.sin(a) * d, 1);
        mn.m.position.set(sp.x, 0, sp.z); mn.m.scale.setScalar(0.01);
        mn.life = 0; mn.arm = 0.35; mn.dmg = 22 + lv * 9; mn.who = 'w-mine';
        mn.trigR = 2.8 * (lv < 3 ? 1 : 2.2) * ar;      // ← Lv3 的質變
        mn.blast = 5.2 * ar;
        mn.ttl = 14 * (P.s.dur || 1);
        scene.add(mn.m);
      }
      SFX.missile();
      fxLaunch(P.pos.x, P.y + 1.6, P.pos.z, P.faceAim, GADGET_C['w-mine']);
    }
  },
  {
    /* 軌道光柱 —— 清場型，玩家不用做任何事，它自己找最密的地方砸。
       **質變在 Lv4：改為優先鎖定精英與踉蹌中的目標。**
       那一級之後它從「清雜魚的工具」變成「處決硬目標的工具」——
       同一把武裝，用途完全換邊。 */
    id: 'w-orbital', n: '軌道光柱', kind: '掛載', axes: ['範圍', '傷害'],
    turn: 4, turnD: '落點從「最密處」改為「精英與踉蹌中的目標」',
    d: function (lv) {
      return '呼叫 ' + (lv < 3 ? 1 : (lv < 5 ? 2 : 3)) + ' 道軌道光柱，落在'
        + (lv < 4 ? '敵人最密處' : '**精英／踉蹌中的目標**');
    },
    cd: function (lv) { return Math.max(4.5, 9.0 - lv * 0.8); },
    fire: function (lv) {
      var n = lv < 3 ? 1 : (lv < 5 ? 2 : 3), picks = [];
      for (var q = 0; q < n; q++) {
        var best = null, bestC = -1;
        for (var i = 0; i < enemies.length; i++) {
          var e = enemies[i], c = 0, tooClose = false;
          for (var p = 0; p < picks.length; p++) {
            if (Math.hypot(e.pos.x - picks[p].x, e.pos.z - picks[p].z) < 10) tooClose = true;
          }
          if (tooClose) continue;
          for (var j = 0; j < enemies.length; j++) {
            if (Math.hypot(enemies[j].pos.x - e.pos.x, enemies[j].pos.z - e.pos.z) < 7) c++;
          }
          /* Lv4 的質變：精英／Boss 加 40 分、踉蹌中再加 40。
             用「加分」而不是「只挑硬目標」—— 場上沒有硬目標時要退回清場行為，
             否則這把武裝在前三分鐘等於不存在。 */
          if (lv >= 4) {
            if (e.T.boss || e.elite) c += 40;
            if (e.staggerT > 0) c += 40;
          }
          if (c > bestC) { bestC = c; best = e; }
        }
        if (!best) break;
        picks.push({ x: best.pos.x, z: best.pos.z });
      }
      if (!picks.length) picks.push({ x: P.pos.x + Math.sin(P.faceAim) * 12, z: P.pos.z + Math.cos(P.faceAim) * 12 });
      for (var w = 0; w < picks.length; w++) {
        var s = strikes.get(); if (!s) break;
        s.m.position.set(picks[w].x, 0, picks[w].z);
        s.radius = (5 + lv * 0.6) * (P.s.area || 1); s.dmg = 40 + lv * 22; s.who = 'w-orbital';
        s.life = 0; s.delay = 0.7 + w * 0.18; s.fired = false;
        s.ring.scale.setScalar(s.radius); s.ring.material.opacity = 0.8;
        s.col.scale.set(s.radius * 1.2, 60, s.radius * 1.2); s.col.material.opacity = 0;
        scene.add(s.m);
      }
      SFX.beam();
    }
  },
  {
    /* 跳彈迴路 —— 衝擊 build 的主力載具。
       **質變在 Lv3：改為優先射向玩家用 TAB 鎖定的目標。**
       這一級把「鎖定」從一個可有可無的按鍵變成 build 的核心操作 ——
       衝擊值會隨時間衰減，所以分散打填不滿，而這把是唯一能被玩家指方向的池子武裝。 */
    id: 'w-chain', n: '跳彈迴路', kind: '掛載', axes: ['冷卻', '衝擊'],
    turn: 3, turnD: '改為優先射向 TAB 鎖定的目標（集中火力填衝擊值）',
    d: function (lv) {
      return '射出跳彈，命中後彈向下一個目標，最多 ' + (1 + lv) + ' 次'
        + (lv < 3 ? '' : '；**優先射向鎖定目標**');
    },
    cd: function (lv) { return Math.max(0.55, 1.5 - lv * 0.19); },
    fire: function (lv) {
      var shots = lv >= 5 ? 2 : 1;
      for (var i = 0; i < shots; i++) {
        /* 第一發給鎖定目標，第二發仍然找最近的 —— 兩發都打同一隻的話
           跳彈的「彈向下一個」就沒有意義了。 */
        var tgt = (lv >= 3 && i === 0 && lockedTarget()) ||
          nearestEnemy(P.pos, 34, P.faceAim + (i ? 1.2 : 0), true);
        if (!tgt) return;
        var a = Math.atan2(tgt.pos.x - P.pos.x, tgt.pos.z - P.pos.z);
        var crit = RNG.next() < P.s.crit;
        spawnBullet(P.pos.x, P.pos.z, a, (10 + lv * 5) * (crit ? P.s.critMul : 1), crit, 56, 0, 1 + lv, 0.5, undefined, 'w-chain');
        fxGadgetFire('w-chain', P.pos.x, P.y + 2.1, P.pos.z, a);
      }
      SFX.shoot();
    }
  },
  {
    /* 衝擊斬波 —— 定向穿透，逼玩家把砲口對準敵人排成一線的方向。
       **質變在 Lv3：從一道變成三向扇形。** 那一級之後它從
       「要瞄準的武器」變成「面向那一側的清場帶」，走位邏輯換掉。 */
    id: 'w-pulse', n: '衝擊斬波', kind: '掛載', axes: ['範圍', '衝擊'],
    turn: 3, turnD: '單向一道 → 面向與兩側共三道',
    d: function (lv) {
      return (lv < 3 ? '朝面向射出一道穿透性斬波' : '朝面向與兩側各射出一道穿透性斬波')
        + '，傷害 ' + (14 + lv * 7);
    },
    cd: function (lv) { return Math.max(1.1, 2.6 - lv * 0.32); },
    fire: function (lv) {
      var angs = lv < 3 ? [0] : [0, 0.9, -0.9];
      var crit = RNG.next() < P.s.crit;
      for (var i = 0; i < angs.length; i++) {
        spawnBullet(P.pos.x, P.pos.z, P.faceAim + angs[i],
          (14 + lv * 7) * (crit ? P.s.critMul : 1), crit, 34, 99, 0,
          (2.2 + lv * 0.14) * (P.s.area || 1), 0.6, 'w-pulse');
        fxGadgetFire('w-pulse', P.pos.x, P.y + 1.9, P.pos.z, P.faceAim + angs[i]);
      }
      /* 斬波是「一道往前推的牆」，所以除了槍口還要一片弧 ——
         它與主砲的區別在覆蓋形狀，形狀要看得見才成立。 */
      fxSlashArc(P.pos.x, P.y + 1.6, P.pos.z, P.faceAim, 3.2 + lv * 0.3, GADGET_C['w-pulse']);
      SFX.slash();
    }
  },
  {
    /* 環繞旋刃（持續型）—— 貼身防禦圈，逼玩家「讓敵人來撞」。
       **質變在 Lv3：半徑開始呼吸**（在 R 與 R×0.55 之間伸縮）。
       在那之前它只掃得到一圈細細的環，貼在身上的敵人反而安全；
       之後它掃過整個圓盤 —— **覆蓋形狀從「環」變成「盤」**，
       那是 VS 差異化手法裡最有效的一種（同一把武器，覆蓋形狀換掉）。 */
    id: 'w-orbit', n: '環繞旋刃', kind: '掛載', axes: ['範圍', '衝擊'],
    turn: 3, turnD: '覆蓋形狀從「環」變成「盤」（半徑開始伸縮）',
    d: function (lv) {
      return (lv < 2 ? 1 : (lv < 4 ? 2 : 3)) + ' 片旋刃繞行，接觸造成 ' + (9 + lv * 5) + ' 傷害'
        + (lv < 3 ? '' : '；**半徑伸縮，掃過整個圓盤**');
    },
    /* **刻意沒有拖尾。** 2026-08-05 加過一版緞帶拖尾（想讓它不那麼單調），
       使用者回報「會有個引線，很像魔法」—— 一條跟著刀刃畫圓的發光線，
       在畫面上讀成法陣的軌跡而不是金屬在轉。單調的解法是把**刃本身**加粗
       （見 `render/gadgets.ts` 的尺寸註解），不是在它後面加一條光。 */
    /* 造型的張角與半寬**照著下面的判定填**（0.55 弧度 / 1.6 公尺）。
       這兩個數字改了造型就要跟著改，否則又會回到「看起來打到但沒打到」。 */
    onEquip: function (o) { o.st = { vis: makeOrbitBlades(3, 0xd8dee6, { span: 0.55, width: 1.6 }), a: 0, hits: [] }; },
    onRemove: function (o) { if (o.st) disposeGadget(o.st.vis); o.st = null; },
    update: function (lv, dt, o) {
      if (!o.st) return;
      var n = lv < 2 ? 1 : (lv < 4 ? 2 : 3);
      var st = o.st;
      st.a += dt * (2.2 + lv * 0.25);
      /* Lv3 起半徑呼吸。`st.a` 已經是累積的相位，直接拿它當呼吸的時鐘，
         不另外開一個計時器 —— 多一個時鐘就多一個「忘了在開局重置」的坑。 */
      var R = (5.5 + lv * 0.5) * (P.s.area || 1);
      if (lv >= 3) R *= 0.775 + 0.225 * Math.sin(st.a * 0.9);
      st.vis.g.position.set(P.pos.x, 0, P.pos.z);
      /* 半徑跟著 R 走。Lv3 起 R 會呼吸，而**質變本身就是「覆蓋形狀從環變成盤」**
         —— 扇形帶不跟著動的話玩家看不到那個質變。 */
      setBladeRadius(st.vis, R);
      for (var b = 0; b < st.vis.blades.length; b++) {
        var on = b < n;
        st.vis.blades[b].visible = on;
        if (!on) continue;
        st.vis.blades[b].rotation.y = st.a + (b / n) * TAU;
      }
      /* 再命中冷卻逐一遞減。用陣列而不是 Map：長度就是場上敵人數的上限，
         而且陣列的走訪順序是決定性的。 */
      for (var h = st.hits.length - 1; h >= 0; h--) {
        st.hits[h].t -= dt;
        if (st.hits[h].t <= 0) st.hits.splice(h, 1);
      }
      var dmg = 9 + lv * 5;
      for (var i = enemies.length - 1; i >= 0; i--) {
        if (i >= enemies.length) i = enemies.length - 1;
        if (i < 0) break;
        var e = enemies[i]; if (!e) continue;
        var dx = e.pos.x - P.pos.x, dz = e.pos.z - P.pos.z;
        var d = Math.hypot(dx, dz);
        if (Math.abs(d - R) > 1.6 + e.r) continue;
        var ang = Math.atan2(dx, dz), hit = false;
        for (var k = 0; k < n; k++) {
          var ba = st.a + (k / n) * TAU;
          /* **一定要走 `angDiff`**，不要在這裡自己寫取模：`ba` 來自無上限累積的
             相位 `st.a`，手寫的單次取模在開局幾秒後就會永久回 false
             （整把武裝的接觸傷害靜默消失）。詳見 `sim/fx.ts` 的 `angDiff`。 */
          if (Math.abs(angDiff(ang, ba)) < 0.55) { hit = true; break; }
        }
        if (!hit) continue;
        var busy = false;
        for (var q = 0; q < st.hits.length; q++) if (st.hits[q].e === e) { busy = true; break; }
        if (busy) continue;
        st.hits.push({ e: e, t: 0.45 });
        var kk = 1 / Math.max(0.6, d);
        damageEnemy(e, dmg, false, dx * kk * 5, dz * kk * 5, 'vortex', undefined, undefined, 'w-orbit');
        /* **接觸的那一下要有回饋。** 在這之前這把武裝完全沒有命中特效 ——
           扣血只表現在傷害數字上，所以刀刃掃過去像什麼都沒發生
           （使用者 2026-08-05：「刀片好像不會造成傷害了」）。
           用最便宜的那一拍（`fxGadgetFire` 4 個額度），而且每隻敵人被
           上面的 `st.hits` 壓在 0.45 秒一次，同屏不會爆量。 */
        fxGadgetFire('w-orbit', e.pos.x, (e.T.y || 1) + 0.4, e.pos.z, ang);
      }
    }
  },
  {
    /* 感應無人機（持續型）—— 唯一「不用玩家管」的武裝，各自找最近的打。
       **質變在 Lv4：多出第二台，而且兩台改為集火玩家的鎖定目標。**
       在那之前它是分散輸出（清場），之後變成集中輸出（打硬目標）——
       同一把武裝在那一級跨過兩條軸線的分界。 */
    id: 'w-drone', n: '感應無人機', kind: '掛載', axes: ['冷卻', '傷害'],
    turn: 4, turnD: '1 台各自索敵 → 2 台集火鎖定目標',
    d: function (lv) {
      return (lv < 4 ? '1 台僚機各自索敵' : '**2 台僚機集火鎖定目標**')
        + '，每 ' + Math.max(0.5, 1.5 - lv * 0.14).toFixed(1) + ' 秒射擊，傷害 ' + (8 + lv * 4);
    },
    onEquip: function (o) {
      o.st = { d: [makeDrone(0x5fe3ff), makeDrone(0xffd24a)], a: 0, cd: [0, 0] };
    },
    onRemove: function (o) {
      if (o.st) for (var i = 0; i < o.st.d.length; i++) disposeGadget(o.st.d[i]);
      o.st = null;
    },
    update: function (lv, dt, o) {
      if (!o.st) return;
      var st = o.st, n = lv < 4 ? 1 : 2;
      st.a += dt * 1.1;
      var rate = Math.max(0.5, 1.5 - lv * 0.14);
      for (var i = 0; i < st.d.length; i++) {
        var g = st.d[i].g, on = i < n;
        g.visible = on;
        if (!on) continue;
        var ang = st.a + (i / n) * TAU;
        g.position.set(P.pos.x + Math.sin(ang) * 4.2, 3.2 + Math.sin(st.a * 2 + i) * 0.3,
          P.pos.z + Math.cos(ang) * 4.2);
        /* Lv4 起優先打玩家鎖定的目標；沒鎖定就退回各自索敵。 */
        var tgt = (lv >= 4 && lockedTarget()) || nearestEnemy(g.position, 30, undefined, false);
        if (tgt) g.lookAt(tgt.pos.x, g.position.y, tgt.pos.z);
        st.cd[i] -= dt;
        st.d[i].muzzle.visible = st.cd[i] > rate - 0.06;
        if (st.cd[i] > 0 || !tgt) continue;
        st.cd[i] = rate;
        var a2 = Math.atan2(tgt.pos.x - g.position.x, tgt.pos.z - g.position.z);
        var crit = RNG.next() < P.s.crit;
        spawnBullet(g.position.x, g.position.z, a2,
          (8 + lv * 4) * (crit ? P.s.critMul : 1), crit, 58, 0, 0, 0.5, undefined, 'w-drone');
      }
    }
  }
  ,
  /* ══ 第二批（2026-08-02）══════════════════════════════════════
     使用者的回饋：「池子選擇仍然太多加數值加倍率，**改玩法的不多**」。
     這四把每一把都逼玩家換一種站位，而不是提供更多輸出：

       斥力力場   逼你**站進怪堆裡**（傷害在身上，離開就沒了）
       後衛散布   補**自動瞄準造成的盲區**（砲口永遠朝目標，背後永遠是空的）
       牽引錨     **不造成傷害**，它把敵人搬到你想要的位置
       反射彈     逼你**貼著場邊打**（撞牆才會反彈，開闊地帶最弱）
     ══════════════════════════════════════════════════════════ */
  {
    /* 斥力力場（持續型）—— 貼身傷害環，**Lv3 起把敵人推開**。
       它的性格是「站進去」：傷害只發生在身邊，而身邊正是最危險的地方。
       質變之後它從純輸出變成生存工具，那一級改變的是「敢不敢貼」。 */
    id: 'w-aura', n: '斥力力場', kind: '掛載', axes: ['範圍', '衝擊'],
    turn: 3, turnD: '開始把範圍內的敵人推開（從純輸出變成控場）',
    d: function (lv) {
      return '身周 ' + (4.2 + lv * 0.5).toFixed(1) + ' 單位持續造成 ' + (7 + lv * 4) + ' 傷害'
        + (lv < 3 ? '' : '，並**持續推開敵人**');
    },
    onEquip: function (o) { o.st = { t: 0, vis: makeFieldRing(0xc9c2b4) }; },
    onRemove: function (o) { if (o.st) disposeGadget(o.st.vis); o.st = null; },
    update: function (lv, dt, o) {
      if (!o.st) return;
      var R = (4.2 + lv * 0.5) * (P.s.area || 1);
      /* 場界每幀跟著玩家與半徑走。這把武裝原本在畫面上只有每 0.5 秒閃一下的環，
         其餘時間看不出它存在 —— 而它 Lv3 的質變（開始推開敵人）整個建立在範圍上。 */
      updateFieldRing(o.st.vis, P.pos.x, P.pos.z, R, dt);
      o.st.t -= dt;
      if (o.st.t > 0) return;
      o.st.t = 0.5;                                   // 每 0.5 秒結算一次，不是每步
      fxShockRing(P.pos.x, P.pos.z, GADGET_C['w-aura'], R * 0.45, R * 1.05, 0.42, 0.5);
      for (var i = enemies.length - 1; i >= 0; i--) {
        if (i >= enemies.length) i = enemies.length - 1;
        if (i < 0) break;
        var e = enemies[i]; if (!e) continue;
        var dx = e.pos.x - P.pos.x, dz = e.pos.z - P.pos.z, d = Math.hypot(dx, dz);
        if (d > R + e.r) continue;
        var k = 1 / Math.max(0.6, d);
        var push = lv < 3 ? 0 : 16;                   // ← Lv3 的質變
        damageEnemy(e, 7 + lv * 4, false, dx * k * push, dz * k * push, 'vortex', undefined, undefined, 'w-aura');
      }
    }
  },
  {
    /* 後衛散布 —— **朝背後**打。
       這把武裝存在的理由是這個遊戲的瞄準是全自動的：砲口永遠朝著目標，
       所以「背後」是一個結構性的盲區，而倖存者類的死法幾乎都是被包夾。
       **Lv4 起改成前後同時**，那一級把它從補洞的工具變成主力。 */
    id: 'w-rear', n: '後衛散布', kind: '掛載', axes: ['範圍', '冷卻'],
    turn: 4, turnD: '從「只打背後」變成「前後同時」',
    d: function (lv) {
      return (lv < 4 ? '朝**背後**' : '朝**前後兩側**') + '扇形射出 ' + (2 + lv) +
        ' 發彈丸，傷害 ' + (11 + lv * 5);
    },
    cd: function (lv) { return Math.max(0.9, 2.2 - lv * 0.26); },
    fire: function (lv) {
      var n = 2 + lv, dirs = lv < 4 ? [Math.PI] : [Math.PI, 0];
      var crit = RNG.next() < P.s.crit;
      for (var d = 0; d < dirs.length; d++) {
        for (var i = 0; i < n; i++) {
          var spread = (i - (n - 1) / 2) * 0.24;
          spawnBullet(P.pos.x, P.pos.z, P.faceAim + dirs[d] + spread,
            (11 + lv * 5) * (crit ? P.s.critMul : 1), crit, 44, 1, 0, 0.5, undefined, 'w-rear');
        }
        fxGadgetFire('w-rear', P.pos.x, P.y + 2.1, P.pos.z, P.faceAim + dirs[d]);
      }
      SFX.shoot();
    }
  },
  {
    /* 牽引錨 —— **它不造成傷害。**
       整個池子裡唯一一把「不輸出」的掛載：它把周圍的敵人拉到玩家前方一點，
       讓地雷、力場、光柱有東西可以打。這是 VS 沒有的動詞（VS 只有推開，沒有拉近），
       而它讓「先佈場再拉人」變成一種真的打法。
       **Lv3 起改為拉到腳下**，那一級之後它跟貼身型武裝（力場、旋刃）直接連動。 */
    id: 'w-tether', n: '牽引錨', kind: '掛載', axes: ['範圍', '冷卻'],
    turn: 3, turnD: '拉點從「前方」改到「腳下」（與貼身型武裝連動）',
    d: function (lv) {
      return '**不造成傷害**：把 ' + (9 + lv * 2).toFixed(0) + ' 單位內的敵人拉向'
        + (lv < 3 ? '前方' : '**腳下**');
    },
    cd: function (lv) { return Math.max(2.4, 5.0 - lv * 0.5); },
    fire: function (lv) {
      var R = (9 + lv * 2) * (P.s.area || 1);
      var tx = lv < 3 ? P.pos.x + Math.sin(P.faceAim) * 6 : P.pos.x;
      var tz = lv < 3 ? P.pos.z + Math.cos(P.faceAim) * 6 : P.pos.z;
      var pulled = 0;
      for (var i = 0; i < enemies.length; i++) {
        var e = enemies[i];
        if (e.T.boss) continue;                        // Boss 拉不動，否則 Boss 戰就沒了
        var dx = tx - e.pos.x, dz = tz - e.pos.z, d = Math.hypot(dx, dz);
        if (d > R || d < 0.5) continue;
        var k = (14 + lv * 4) / d;
        e.vel.x += dx * k; e.vel.z += dz * k;
        pulled++;
      }
      if (pulled) {
        SFX.dash();
        /* 收束環 + 往內飛的火花：牽引錨不造成傷害，它唯一的回饋就是
           「東西被拉過來了」，那件事必須看得到。 */
        fxShockRing(tx, tz, GADGET_C['w-tether'], R, 1.2, 0.42, 0.55);
        for (var si = 0; si < 6; si++) {
          var sa = (si / 6) * TAU;
          fxSparks(tx + Math.sin(sa) * R * 0.8, 1.2, tz + Math.cos(sa) * R * 0.8,
            1, sa + Math.PI, 0.2, GADGET_C['w-tether'], R * 2.2);
        }
      }
    }
  },
  {
    /* 反射彈 —— 撞到場邊會反彈，而且活得很久。
       它的性格是「貼著場邊打」：在場中央它跟普通子彈沒兩樣，
       在角落它會來回掃好幾趟。**Lv4 起反彈時分裂**，那一級讓角落變成處刑場。
       （對應 VS 的 Runetracer —— 那把也是靠反彈製造「賴在原地」的價值。） */
    id: 'w-ricochet', n: '反射彈', kind: '掛載', axes: ['傷害', '冷卻'],
    turn: 4, turnD: '反彈時分裂成兩發',
    d: function (lv) {
      return '射出會**沿場邊反彈**的彈丸（最多 ' + (1 + lv) + ' 次），傷害 ' + (13 + lv * 6)
        + (lv < 4 ? '' : '；**反彈時分裂**');
    },
    cd: function (lv) { return Math.max(1.0, 2.4 - lv * 0.3); },
    fire: function (lv) {
      var tgt = nearestEnemy(P.pos, 36, P.faceAim, true);
      var a = tgt ? Math.atan2(tgt.pos.x - P.pos.x, tgt.pos.z - P.pos.z) : P.faceAim;
      var crit = RNG.next() < P.s.crit;
      var b = spawnBullet(P.pos.x, P.pos.z, a, (13 + lv * 6) * (crit ? P.s.critMul : 1), crit, 48, 2, 0, 0.5, undefined, 'w-ricochet');
      if (b) { b.bounce = 1 + lv; b.split = lv >= 4 ? 1 : 0; b.ttl = 3.4; }
      fxGadgetFire('w-ricochet', P.pos.x, P.y + 2.1, P.pos.z, a);
      SFX.shoot();
    }
  }
];

export function weaponById(id) {
  for (var i = 0; i < WEAPON_POOL.length; i++) if (WEAPON_POOL[i].id === id) return WEAPON_POOL[i];
  return null;
}
